/**
 * Edit form for every help phone and form URL.
 * The open interface does not limit which rows the superadmin can change.
 */
import * as React from 'react';
import { LifeBuoy } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { Input } from '@/components/forms/Input';
import { toast } from '@/components/ui/toast';
import { getHelpSupportAdmin, replaceHelpSupport } from '@/services/tutorials.service';
import type { HelpSupportContextEntry } from '@/types/tutorial.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { HELP_SUPPORT_FORM_SECTIONS, tutorialKeys } from './tutorial-configs';
import {
  helpSupportEntriesByKey,
  helpSupportFallbackCatalog,
  isHelpSupportCatalogValid,
  updateHelpSupportEntry,
} from './tutorial-form-utils';

/** Props for {@link EditHelpSupportFormView}. */
type EditHelpSupportFormViewProps = Readonly<{
  onClose: () => void;
  onSuccess: () => void;
}>;

/** Input change on a help-support text field. */
type HelpSupportFieldChange = React.ChangeEvent<HTMLInputElement>;
/** One block in the help-support edit form. */
type HelpSupportFormSection = (typeof HELP_SUPPORT_FORM_SECTIONS)[number];
/** Draft catalog while the superadmin edits, or none. */
type HelpSupportDraft = HelpSupportContextEntry[] | null;

/** Props for one help-support platform/mode block. */
type HelpSupportSectionProps = Readonly<{
  section: HelpSupportFormSection;
  entry: HelpSupportContextEntry | undefined;
  onUrlChange: (event: HelpSupportFieldChange) => void;
  onPhoneChange: (event: HelpSupportFieldChange) => void;
}>;

/** One help block: form URL then referent phone. */
function HelpSupportSection({
  section,
  entry,
  onUrlChange,
  onPhoneChange,
}: HelpSupportSectionProps): React.ReactElement {
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <LifeBuoy className="w-4 h-4" />
        {section.title}
      </h4>
      <div className="grid grid-cols-1 gap-4">
        <Input
          label="Lien du formulaire"
          value={entry?.url ?? ''}
          onChange={onUrlChange}
          placeholder="https://"
          required
        />
        <Input
          label="Téléphone du référent"
          value={entry?.phone ?? ''}
          onChange={onPhoneChange}
          maxLength={50}
          required
        />
      </div>
    </div>
  );
}

/** Form URL then phone, for administration and users, in-person and remote. */
export function EditHelpSupportFormView({
  onClose,
  onSuccess,
}: EditHelpSupportFormViewProps): React.ReactElement {
  const [draft, setDraft] = React.useState<HelpSupportDraft>(null);

  const helpQuery = useQuery({
    queryKey: tutorialKeys.helpAdmin,
    queryFn: getHelpSupportAdmin,
  });

  const entries = draft ?? helpQuery.data ?? helpSupportFallbackCatalog();
  const byKey = helpSupportEntriesByKey(entries);
  const isValid = isHelpSupportCatalogValid(entries);

  const saveMutation = useMutation({
    mutationFn: () => replaceHelpSupport(entries),
    onSuccess: () => {
      toast.success("Formulaire d'aide mis à jour");
      onSuccess();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = () => {
    if (!isValid) return;
    saveMutation.mutate();
  };

  function applyPhoneChange(key: string, event: HelpSupportFieldChange): void {
    setDraft(updateHelpSupportEntry(entries, key, 'phone', event.target.value));
  }

  function applyUrlChange(key: string, event: HelpSupportFieldChange): void {
    setDraft(updateHelpSupportEntry(entries, key, 'url', event.target.value));
  }

  function renderSection(section: HelpSupportFormSection): React.ReactNode {
    function handleUrlChange(event: HelpSupportFieldChange): void {
      applyUrlChange(section.key, event);
    }
    function handlePhoneChange(event: HelpSupportFieldChange): void {
      applyPhoneChange(section.key, event);
    }
    return (
      <HelpSupportSection
        key={section.key}
        section={section}
        entry={byKey[section.key]}
        onUrlChange={handleUrlChange}
        onPhoneChange={handlePhoneChange}
      />
    );
  }

  return (
    <SlidePanelForm
      formType="edit"
      title="Modifier l'aide"
      subtitle="Administration et usagers, présentiel et distanciel"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={saveMutation.isPending}
      isValid={isValid}
      isLoading={helpQuery.isLoading && !helpQuery.data}
    >
      {HELP_SUPPORT_FORM_SECTIONS.map(renderSection)}
    </SlidePanelForm>
  );
}
